import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../utils/api";
import { setRaygunUser, clearRaygunUser } from "../lib/raygun";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is authenticated via httpOnly cookie
    // No need to check localStorage anymore - token is in secure cookie
    verifyToken();
  }, []);

  const verifyToken = async () => {
    try {
      const response = await api.post("/auth/verify");
      setUser(response.data.user);
      setRaygunUser(response.data.user);
    } catch (error) {
      // No need to remove localStorage token - using httpOnly cookies now
      // Cookie will be cleared by calling /auth/logout
      clearRaygunUser();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post("/auth/login", { email, password });
      const { user: userData } = response.data;

      // Token is now stored in httpOnly cookie by the server
      // No need to handle it in localStorage
      setUser(userData);
      setRaygunUser(userData);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Login failed",
      };
    }
  };

  const logout = async () => {
    try {
      // Call backend to clear httpOnly cookies
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout API call failed:", error);
      // Continue with logout even if API call fails
    }

    setUser(null);
    clearRaygunUser();
  };

  useEffect(() => {
    if (loading) {
      return;
    }

    if (user) {
      setRaygunUser(user);
    } else {
      clearRaygunUser();
    }
  }, [user, loading]);

  const bootstrapSession = (token, userData) => {
    if (!userData) {
      return;
    }

    // Token is now in httpOnly cookie, no need to store in localStorage
    // This function is kept for backwards compatibility but doesn't handle tokens anymore
    setUser(userData);
    setRaygunUser(userData);
  };

  const value = {
    user,
    login,
    logout,
    loading,
    bootstrapSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
